import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaFacebook, FaInstagram, FaLinkedin, FaMusic, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { authAPI, securityAPI } from '../utils/api';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';

const LandingPage = ({ setIsAuthenticated }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email' | 'code'
  const [resetCode, setResetCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isForgotPassword) {
        if (resetStep === 'email') {
          // Step 1: Request reset code
          const response = await securityAPI.requestReset({ email: formData.email });
          toast.success('Reset code sent! Check your email.');
          // In development, show the debug code
          if (response.data.debug_code) {
            setResetCode(response.data.debug_code);
            toast.info(`Dev mode — code: ${response.data.debug_code}`, { duration: 10000 });
          }
          setResetStep('code');
          setLoading(false);
          return;
        }
        // Step 2: Verify code + set new password
        const response = await securityAPI.resetPassword({
          email: formData.email,
          code: resetCode,
          new_password: formData.password
        });
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.setItem('token', response.data.token);
        toast.success('Password reset! You are now logged in.');
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else if (isLogin) {
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.setItem('token', response.data.token);
        toast.success('Welcome back!');
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        const response = await authAPI.register(formData);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        sessionStorage.setItem('token', response.data.token);
        toast.success('Account created successfully!');
        setIsAuthenticated(true);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div className="hero-glow absolute inset-0 pointer-events-none" style={{ background: 'none' }} />
      
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="relative z-10 p-6 md:p-8 flex justify-between items-center bg-white"
      >
        <div className="flex items-center space-x-3">
          <img 
            src="/talkinpoint-logo.svg" 
            alt="TalkinPoint" 
            className="h-10 w-auto"
          />
        </div>
        <button
          data-testid="get-started-btn"
          onClick={() => setShowAuth(true)}
          className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-gray-900 hover:opacity-90 font-bold rounded-full px-6 py-3 transition-all hover:scale-105 active:scale-95 glow-blue"
        >
          Get Started
        </button>
      </motion.nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center space-y-8"
        >
          <h1 className="flex justify-center items-center">
            <img 
              src="/talkinpoint-icon.svg" 
              alt="TalkinPoint" 
              className="h-24 md:h-32 lg:h-40 w-auto"
            />
          </h1>
          <p className="text-2xl md:text-3xl text-gray-900 font-nunito font-extrabold max-w-3xl mx-auto leading-relaxed">
            The Ultimate Platform
          </p>
        </motion.div>

        {/* From GDM Footer */}
        <div className="mt-20 text-center pb-8">
          <div className="flex items-center justify-center space-x-4 mb-4">
            <Link to="/privacy" className="text-gray-400 text-sm hover:text-[#4A90E2] transition-colors" data-testid="landing-privacy-link">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/terms" className="text-gray-400 text-sm hover:text-[#4A90E2] transition-colors" data-testid="landing-terms-link">Terms of Use</Link>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-gray-400 text-sm font-medium tracking-wide">from</span>
            <a href="https://www.gdmhq.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img 
                src="/gdm-logo.png" 
                alt="GDM" 
                className="h-5 w-auto"
              />
            </a>
          </div>
        </div>
      </div>

      {showAuth && (
        <div
          className="fixed inset-0 bg-gray-900/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setShowAuth(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-gray-300 rounded-2xl p-8 max-w-md w-full shadow-2xl"
            data-testid="auth-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-outfit font-bold text-gray-900">
                {isForgotPassword ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Join TalkinPoint'}
              </h2>
              <button
                data-testid="close-auth-modal"
                onClick={() => { setShowAuth(false); setIsForgotPassword(false); }}
                className="text-gray-600 hover:text-gray-900 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Email</label>
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg h-12 px-4 text-gray-900 placeholder:text-gray-900/20 transition-all outline-none"
                  placeholder="you@example.com"
                  required
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <label className="text-sm text-gray-600 mb-2 block">Username</label>
                    <input
                      data-testid="auth-username-input"
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg h-12 px-4 text-gray-900 placeholder:text-gray-900/20 transition-all outline-none"
                      placeholder="johndoe"
                      required={!isLogin}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-2 block">Full Name</label>
                    <input
                      data-testid="auth-fullname-input"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg h-12 px-4 text-gray-900 placeholder:text-gray-900/20 transition-all outline-none"
                      placeholder="John Doe"
                      required={!isLogin}
                    />
                  </div>
                </>
              )}

              {isForgotPassword && resetStep === 'code' && (
                <div>
                  <label className="text-sm text-gray-600 mb-2 block">Reset Code</label>
                  <input
                    data-testid="auth-reset-code-input"
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg h-12 px-4 text-gray-900 placeholder:text-gray-900/20 transition-all outline-none tracking-widest text-center text-lg font-bold"
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Enter the 6-digit code sent to your email</p>
                </div>
              )}

              {(!isForgotPassword || resetStep === 'code') && (
              <div>
                <label className="text-sm text-gray-600 mb-2 block">
                  {isForgotPassword ? 'New Password' : 'Password'}
                </label>
                <div className="relative">
                  <input
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-gray-100 border border-gray-300 focus:border-[#4A90E2] focus:ring-1 focus:ring-[#4A90E2] rounded-lg h-12 px-4 pr-11 text-gray-900 placeholder:text-gray-900/20 transition-all outline-none"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                {isLogin && !isForgotPassword && (
                  <button
                    type="button"
                    onClick={() => { setIsForgotPassword(true); setResetStep('email'); setResetCode(''); }}
                    className="text-xs text-[#4A90E2] hover:underline mt-1.5 block"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-gray-900 hover:opacity-90 font-bold rounded-full h-12 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                {loading ? 'Please wait...' : isForgotPassword ? (resetStep === 'email' ? 'Send Reset Code' : 'Reset Password') : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            {isForgotPassword ? (
              <p className="text-center text-gray-600 mt-6">
                Remember your password?
                <button
                  onClick={() => setIsForgotPassword(false)}
                  className="text-[#4DD9E8] hover:text-[#4A90E2] ml-2 font-medium"
                >
                  Sign In
                </button>
              </p>
            ) : (
            <p className="text-center text-gray-600 mt-6">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="text-[#4DD9E8] hover:text-[#4A90E2] ml-2 font-medium"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
            )}

            {!isLogin && (
              <p className="text-center text-xs text-gray-400 mt-4" data-testid="auth-legal-text">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="text-[#4A90E2] hover:underline" onClick={() => setShowAuth(false)}>Terms of Use</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-[#4A90E2] hover:underline" onClick={() => setShowAuth(false)}>Privacy Policy</Link>.
              </p>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
